import { MapContainer, TileLayer, Polyline, useMap, CircleMarker, Popup } from "react-leaflet";
import { memo, useEffect, useState, useRef, useCallback, useMemo } from "react";
import "leaflet/dist/leaflet.css";
import './App.css';

const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY;
const CARTO_TILE_URL = `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`;

// Linien-Konfigurationen (alphabetisch sortiert)
const LINE_CONFIGS = [
  { id: "line1", name: "Linie 1", color: "#e30613", routeUrl: "/line1_route.json", stopsUrl: "/line1_stops_snapped.json", isStraightRoute: true },
  { id: "line2", name: "Linie 2", color: "#008080", routeUrl: "/line2_route.json", stopsUrl: "/line2_stops_snapped.json", isStraightRoute: true },
  { id: "line3", name: "Linie 3", color: "#0065ae", routeUrl: "/line3_route.json", stopsUrl: "/line3_stops_snapped.json", isStraightRoute: true },
  { id: "line4", name: "Linie 4", color: "#8e44ad", routeUrl: "/line4_route.json", stopsUrl: "/line4_stops_snapped.json", isStraightRoute: true },
  { id: "line5", name: "Linie 5", color: "#ff6600", routeUrl: "/line5_route.json", stopsUrl: "/line5_stops_snapped.json", isStraightRoute: false },
  { id: "line6", name: "Linie 6", color: "#27ae60", routeUrl: "/line6_route.json", stopsUrl: "/line6_stops_snapped.json", isStraightRoute: true },
  { id: "line7", name: "Linie 7", color: "#2980b9", routeUrl: "/line7_route.json", stopsUrl: "/line7_stops_snapped.json", isStraightRoute: true },
  { id: "line8", name: "Linie 8", color: "#c0392b", routeUrl: "/line8_route.json", stopsUrl: "/line8_stops_snapped.json", isStraightRoute: true },
  { id: "line9", name: "Linie 9", color: "#34495e", routeUrl: "/line9_route.json", stopsUrl: "/line9_stops_snapped.json", isStraightRoute: true },
  { id: "line10", name: "Linie 10", color: "#00a651", routeUrl: "/line10_route.json", stopsUrl: "/line10_stops_snapped.json", isStraightRoute: true },
  { id: "line11", name: "Linie 11", color: "#e67e22", routeUrl: "/line11_route.json", stopsUrl: "/line11_stops_snapped.json", isStraightRoute: true },
  { id: "line12", name: "Linie 12", color: "#16a085", routeUrl: "/line12_route.json", stopsUrl: "/line12_stops_snapped.json", isStraightRoute: true },
  { id: "line13", name: "Linie 13", color: "#9b59b6", routeUrl: "/line13_route.json", stopsUrl: "/line13_stops_snapped.json", isStraightRoute: true },
].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

function normalizeStopName(name) {
  if (!name) return "";
  return name
    .replace(/^Hannover\//i, "")
    .replace(/\s*\(U-Bahn\)/i, "")
    .trim();
}

function FixMap() {
  const map = useMap();

  useEffect(() => {
    const timeout = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => clearTimeout(timeout);
  }, [map]);

  return null;
}

// Separate Popup-Komponente, die Daten erst beim Öffnen / Rendern aus Refs liest
function StopPopup({ cfg, stop, stopIdx, lData, stopVisitsRef, tramsRef }) {
  const stopKey = `${cfg.id}_${stopIdx}`;
  const visits = stopVisitsRef.current[stopKey] || { total: 0, dirA: 0, dirB: 0, distinct: new Set(), distinctA: new Set(), distinctB: new Set() };
  const stopRouteIdx = lData.stopIndices ? lData.stopIndices[stopIdx] : -1;
  const currentTrams = tramsRef.current || [];
  const tramsHere = currentTrams.filter(t => t.line === cfg.id && t.index === stopRouteIdx && t.pause > 0);
  const tramsHereA = tramsHere.filter(t => t.direction === 1).length;
  const tramsHereB = tramsHere.filter(t => t.direction === -1).length;

  return (
    <div style={{ minWidth: 180, fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: cfg.color }} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>{stop.name}</span>
      </div>
      <div style={{ color: "#888", fontSize: 11 }}>{cfg.name}</div>
      <div style={{ color: "#888", fontSize: 11 }}>Lat: {stop.lat.toFixed(6)}</div>
      <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>Lon: {stop.lon.toFixed(6)}</div>

      <div style={{ borderTop: "1px solid #ddd", margin: "6px 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#666" }}>Gerade hier</span>
        <strong>{tramsHere.length}</strong>
      </div>
      {tramsHere.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
            <span>→ Richtung A</span>
            <span>{tramsHereA}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
            <span>← Richtung B</span>
            <span>{tramsHereB}</span>
          </div>
        </>
      )}

      <div style={{ borderTop: "1px solid #eee", margin: "4px 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#666" }}>Passiert (gesamt)</span>
        <strong>{Math.floor(visits.total / 2)}</strong>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
        <span>→ Richtung A</span>
        <span>{Math.floor(visits.dirA / 2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
        <span>← Richtung B</span>
        <span>{Math.floor(visits.dirB / 2)}</span>
      </div>
      <div style={{ borderTop: "1px solid #eee", margin: "4px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "#666" }}>Distinkte Bahnen</span>
        <strong>{visits.distinct.size}</strong>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
        <span>→ Richtung A</span>
        <span>{visits.distinctA.size}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", paddingLeft: 8 }}>
        <span>← Richtung B</span>
        <span>{visits.distinctB.size}</span>
      </div>
    </div>
  );
}

// Statische Ebenen memoizen, damit die 69 Haltestellen & Polylines nicht bei jedem Bahn-Tick re-rendern
const StaticMapLayers = memo(function StaticMapLayers({ linesData, visibleLines, stopVisitsRef, tramsRef }) {
  return (
    <>
      {LINE_CONFIGS.map(cfg => {
        if (!visibleLines[cfg.id]) return null;
        const lData = linesData[cfg.id];
        if (!lData) return null;

        return (
          <div key={cfg.id}>
            {/* Komplette Strecke */}
            {lData.routeSegments && lData.routeSegments.length > 0 && (
              <Polyline
                positions={lData.routeSegments}
                pathOptions={{ color: cfg.color, opacity: 0.35, weight: 4 }}
              />
            )}

            {/* Haltestellen */}
            {lData.stops && lData.stops.map((stop, stopIdx) => {
              const stopKey = `${cfg.id}_${stopIdx}`;
              return (
                <CircleMarker
                  key={stopKey}
                  center={[stop.lat, stop.lon]}
                  radius={3.5}
                  pathOptions={{ color: cfg.color, fillColor: "#ffffff", fillOpacity: 1, weight: 1.5 }}
                >
                  <Popup>
                    <StopPopup
                      cfg={cfg}
                      stop={stop}
                      stopIdx={stopIdx}
                      lData={lData}
                      stopVisitsRef={stopVisitsRef}
                      tramsRef={tramsRef}
                    />
                  </Popup>
                </CircleMarker>
              );
            })}
          </div>
        );
      })}
    </>
  );
});

function sortRoute(points) {
  if (!points || points.length <= 1) return points || [];

  const sorted = [points[0]];
  const remaining = points.slice(1);

  while (remaining.length) {
    const last = sorted[sorted.length - 1];
    let closestIndex = 0;
    let closestDist = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const [lat, lon] = remaining[i];
      const dLat = lat - last[0];
      const dLon = lon - last[1];
      const dist = dLat * dLat + dLon * dLon;

      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }

    sorted.push(remaining.splice(closestIndex, 1)[0]);
  }

  return sorted;
}

function splitIntoSegments(points, maxStepSquared = 0.0001) {
  if (!points || points.length === 0) return [];

  const segments = [];
  let currentSegment = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    const dLat = curr[0] - prev[0];
    const dLon = curr[1] - prev[1];
    const dist2 = dLat * dLat + dLon * dLon;

    if (dist2 > maxStepSquared) {
      if (currentSegment.length > 1) {
        segments.push(currentSegment);
      }
      currentSegment = [curr];
    } else {
      currentSegment.push(curr);
    }
  }

  if (currentSegment.length > 1) {
    segments.push(currentSegment);
  }

  return segments;
}

// Route anhand der Haltestellen-Reihenfolge sortieren
function sortRouteByStops(points, stops) {
  if (!points.length || stops.length < 2) return sortRoute(points);

  // Max. erlaubte Entfernung² zur Haltestellenpolylinie (~50m ≈ 0.0007°)
  const MAX_DIST_SQUARED = 0.0000005;

  const withProgress = points.map(pt => {
    let bestProgress = 0;
    let bestDist = Infinity;

    for (let s = 0; s < stops.length - 1; s++) {
      const ax = stops[s].lat, ay = stops[s].lon;
      const bx = stops[s + 1].lat, by = stops[s + 1].lon;
      const dx = bx - ax, dy = by - ay;
      const len2 = dx * dx + dy * dy;

      let t = len2 > 0 ? ((pt[0] - ax) * dx + (pt[1] - ay) * dy) / len2 : 0;
      t = Math.max(0, Math.min(1, t));

      const px = ax + t * dx;
      const py = ay + t * dy;
      const dist = (pt[0] - px) * (pt[0] - px) + (pt[1] - py) * (pt[1] - py);
      const progress = s + t;

      if (dist < bestDist) {
        bestDist = dist;
        bestProgress = progress;
      }
    }

    return { pt, progress: bestProgress, dist: bestDist };
  });

  // Punkte entfernen, die zu weit von der Strecke abweichen
  const filtered = withProgress.filter(x => x.dist < MAX_DIST_SQUARED);

  filtered.sort((a, b) => a.progress - b.progress);
  return filtered.map(x => x.pt);
}

// Lücken in der Route durch Interpolation schließen
function bridgeGaps(points, maxStepSquared = 0.0001) {
  if (!points || points.length <= 1) return points || [];

  const result = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const dLat = curr[0] - prev[0];
    const dLon = curr[1] - prev[1];
    const dist2 = dLat * dLat + dLon * dLon;

    if (dist2 > maxStepSquared) {
      // Interpoliere Zwischenpunkte
      const stepSize = Math.sqrt(maxStepSquared) * 0.8;
      const dist = Math.sqrt(dist2);
      const steps = Math.ceil(dist / stepSize);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        result.push([prev[0] + dLat * t, prev[1] + dLon * t]);
      }
    }

    result.push(curr);
  }

  return result;
}

function App() {
  const [paused, setPaused] = useState(false);
  const [showStatsBox, setShowStatsBox] = useState(false);
  const [statsBoxData, setStatsBoxData] = useState([]);
  const [linesData, setLinesData] = useState({});
  const [visibleLines, setVisibleLines] = useState(() => {
    const initial = {};
    LINE_CONFIGS.forEach(l => {
      initial[l.id] = true;
    });
    return initial;
  });
  const [trams, setTrams] = useState([]);
  const [speedMultiplier, setSpeedMultiplier] = useState(100);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [spawnMode, setSpawnMode] = useState("stoecken"); // "both" or "stoecken" (single terminal)
  const [spawnInterval, setSpawnInterval] = useState(10); // 10 or 15 minutes
  const [showInfo, setShowInfo] = useState(false);

  const [totalDeparted, setTotalDeparted] = useState({});
  const [totalArrived, setTotalArrived] = useState({});
  const [simElapsedMin, setSimElapsedMin] = useState(0);

  const nextTramId = useRef(0);
  const tickCounter = useRef(0);
  const audioCtxRef = useRef(null);
  const lastSpawnSoundTimeRef = useRef(0);
  const lastDespawnSoundTimeRef = useRef(0);
  // Per-stop visit counters: { [`${lineId}_${stopIndex}`]: { total, dirA, dirB, distinct, distinctA, distinctB, stop, normalizedName, lineId } }
  const stopVisitsRef = useRef({});
  const tramsRef = useRef(trams);
  tramsRef.current = trams;

  const speedMultiplierRef = useRef(speedMultiplier);
  speedMultiplierRef.current = speedMultiplier;
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const spawnModeRef = useRef(spawnMode);
  spawnModeRef.current = spawnMode;
  const spawnIntervalRef = useRef(spawnInterval);
  spawnIntervalRef.current = spawnInterval;
  const linesDataRef = useRef(linesData);
  linesDataRef.current = linesData;

  const totalDepartedRef = useRef({});
  const totalArrivedRef = useRef({});

  // Trigger alle 2h (Simulationszeit)
  useEffect(() => {
    if (simElapsedMin === 0) return;
    if (simElapsedMin % 120 === 0) {
      // Top 5 meistbesuchte Haltestellen linienübergreifend aggregieren
      const activeIds = Object.keys(visibleLines).filter(id => visibleLines[id]);
      const aggregated = {};
      Object.values(stopVisitsRef.current)
        .filter(v => activeIds.includes(v.lineId))
        .forEach(v => {
          const normName = v.normalizedName || normalizeStopName(v.stop.name);
          const count = Math.floor(v.total / 2);
          if (!aggregated[normName]) {
            aggregated[normName] = { name: normName, total: 0 };
          }
          aggregated[normName].total += count;
        });

      const stats = Object.values(aggregated)
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
      setStatsBoxData(stats);
      setShowStatsBox(true);
      const timeout = setTimeout(() => setShowStatsBox(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [simElapsedMin, visibleLines]);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    return ctx;
  }, []);

  // AudioContext bei erster User-Interaktion entsperren (Mobile)
  useEffect(() => {
    const unlock = () => {
      getAudioCtx();
    };
    document.addEventListener("touchstart", unlock);
    document.addEventListener("click", unlock);
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [getAudioCtx]);

  const playSpawnSound = useCallback(() => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      const t = ctx.currentTime;
      osc.frequency.setValueAtTime(880, t);
      osc.frequency.exponentialRampToValueAtTime(1760, t + 0.08);
      osc.frequency.exponentialRampToValueAtTime(1320, t + 0.2);
      gain.gain.setValueAtTime(0.15, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.3);
    } catch (e) {}
  }, [getAudioCtx]);

  const playDespawnSound = useCallback(() => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "triangle";
      const t = ctx.currentTime;
      osc.frequency.setValueAtTime(660, t);
      osc.frequency.exponentialRampToValueAtTime(220, t + 0.35);
      gain.gain.setValueAtTime(0.16, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.start(t);
      osc.stop(t + 0.4);
    } catch (e) {}
  }, [getAudioCtx]);

  const STOP_PAUSE_STEPS = 20;
  const PULSE_STEPS = 6;
  const LIFETIME_TICKS = 14400; // 4 Stunden = 4 × 3600
  const MIN_TRAM_DISTANCE = 150;

  // Lade Routen und Haltestellen für alle Linien
  useEffect(() => {
    Promise.all(
      LINE_CONFIGS.map(cfg =>
        Promise.all([
          fetch(process.env.PUBLIC_URL + cfg.routeUrl).then(r => r.json()),
          fetch(process.env.PUBLIC_URL + cfg.stopsUrl).then(r => r.json()),
        ]).then(([rawRoute, stops]) => {
          stops.forEach(s => {
            s.normalizedName = normalizeStopName(s.name);
          });
          let sorted = rawRoute;
          if (!cfg.isStraightRoute) {
            sorted = sortRouteByStops(rawRoute, stops);
          }
          const bridged = bridgeGaps(sorted);
          const segments = splitIntoSegments(sorted);

          const stopIndices = stops.map(stop => {
            let bestIdx = 0;
            let bestDist = Infinity;
            for (let i = 0; i < bridged.length; i++) {
              const [lat, lon] = bridged[i];
              const dLat = lat - stop.lat;
              const dLon = lon - stop.lon;
              const dist2 = dLat * dLat + dLon * dLon;
              if (dist2 < bestDist) {
                bestDist = dist2;
                bestIdx = i;
              }
            }
            return bestIdx;
          });

          return {
            id: cfg.id,
            name: cfg.name,
            color: cfg.color,
            rawRoute,
            stops,
            route: sorted,
            routeSegments: segments,
            animationRoute: bridged,
            stopIndices,
          };
        })
      )
    ).then(loadedLines => {
      const dataMap = {};
      loadedLines.forEach(l => {
        dataMap[l.id] = l;
      });
      setLinesData(dataMap);
    });
  }, []);

  // Trams initialisieren bei Spawn-Modus/Takt/Daten-Wechsel
  useEffect(() => {
    const lineEntries = Object.entries(linesData);
    if (lineEntries.length === 0) return;

    nextTramId.current = 0;
    tickCounter.current = 0;
    stopVisitsRef.current = {};
    const initDeparted = {};
    lineEntries.forEach(([lineId]) => {
      initDeparted[lineId] = spawnMode === "both" ? 2 : 1;
    });
    totalDepartedRef.current = initDeparted;
    totalArrivedRef.current = {};
    setTotalDeparted(initDeparted);
    setTotalArrived({});
    setSimElapsedMin(0);

    const initialTrams = [];
    lineEntries.forEach(([lineId, lData]) => {
      if (!lData.animationRoute || lData.animationRoute.length === 0) return;
      const lastIdx = lData.animationRoute.length - 1;
      initialTrams.push({
        id: nextTramId.current++,
        line: lineId,
        index: 0,
        direction: 1,
        pause: 0,
        pulse: 0,
        birthTick: 0,
      });
      if (spawnMode === "both") {
        initialTrams.push({
          id: nextTramId.current++,
          line: lineId,
          index: lastIdx,
          direction: -1,
          pause: 0,
          pulse: 0,
          birthTick: 0,
        });
      }
    });

    tramsRef.current = initialTrams;
    setTrams(initialTrams);
  }, [linesData, spawnMode, spawnInterval]);

  // Simulation Frame-Loop (flüssige 40 FPS mit Sub-Tick-Akkumulator bei allen Geschwindigkeiten)
  const FRAME_INTERVAL_MS = 25;

  useEffect(() => {
    const lineKeys = Object.keys(linesData);
    if (lineKeys.length === 0) return;

    let accumulatedTicks = 0;
    let lastTime = performance.now();

    const interval = setInterval(() => {
      if (pausedRef.current) {
        lastTime = performance.now();
        return;
      }

      const now = performance.now();
      const dt = Math.min(now - lastTime, 100);
      lastTime = now;

      // Berechne abgelaufene Simulationsticks anhand der Geschwindigkeit
      accumulatedTicks += (speedMultiplierRef.current * dt) / 1000;
      const ticksToRun = Math.floor(accumulatedTicks);
      if (ticksToRun <= 0) return;
      accumulatedTicks -= ticksToRun;

      let spawnedCount = 0;
      let despawnedCount = 0;
      const currentLinesData = linesDataRef.current;
      const currentSpawnMode = spawnModeRef.current;
      const spawnIntervalTicks = spawnIntervalRef.current * 60;

      let changedDeparted = false;
      let changedArrived = false;

      let currentTrams = tramsRef.current || [];

      for (let i = 0; i < ticksToRun; i++) {
        const prevTick = tickCounter.current;
        tickCounter.current++;
        const currentTick = tickCounter.current;

        // 1. Alle Bahnen bewegen
        currentTrams = currentTrams.map(tram => {
          const lineInfo = currentLinesData[tram.line];
          if (!lineInfo || !lineInfo.animationRoute || lineInfo.animationRoute.length === 0) {
            return tram;
          }

          const { id, line, index, direction, pause, pulse, birthTick } = tram;
          const lastIndex = lineInfo.animationRoute.length - 1;

          if (pause > 0) {
            return { id, line, index, direction, pause: pause - 1, pulse: Math.max(pulse - 1, 0), birthTick };
          }

          let nextIndex = index + direction;
          let nextDir = direction;

          // An Endstation umkehren
          if (nextIndex > lastIndex) {
            nextIndex = lastIndex;
            nextDir = -1;
          } else if (nextIndex < 0) {
            nextIndex = 0;
            nextDir = 1;
          }

          let nextPause = 0;
          let nextPulse = Math.max(pulse - 1, 0);

          // Zähle Haltestellen-Besuche
          const step = direction;
          if (index !== nextIndex) {
            for (let idx = index + step; step > 0 ? idx <= nextIndex : idx >= nextIndex; idx += step) {
              const isSpawnStart = birthTick === 0 && idx === index;
              const stopListIdx = lineInfo.stopIndices.indexOf(idx);
              if (stopListIdx !== -1 && !isSpawnStart) {
                if (idx === nextIndex) {
                  nextPause = STOP_PAUSE_STEPS;
                  nextPulse = PULSE_STEPS;
                }
                const stopKey = `${line}_${stopListIdx}`;
                const sv = stopVisitsRef.current;
                if (!sv[stopKey]) {
                  const st = lineInfo.stops[stopListIdx];
                  sv[stopKey] = {
                    total: 0,
                    dirA: 0,
                    dirB: 0,
                    distinct: new Set(),
                    distinctA: new Set(),
                    distinctB: new Set(),
                    stop: st,
                    normalizedName: st.normalizedName || normalizeStopName(st.name),
                    lineId: line,
                  };
                }
                sv[stopKey].total++;
                if (direction === 1) sv[stopKey].dirA++;
                else sv[stopKey].dirB++;

                sv[stopKey].distinct.add(id);
                if (direction === 1) sv[stopKey].distinctA.add(id);
                else sv[stopKey].distinctB.add(id);
              }
            }
          }

          return { id, line, index: nextIndex, direction: nextDir, pause: nextPause, pulse: nextPulse, birthTick };
        });

        // 2. Bahnen nach Schichtende (4 Stunden) entfernen
        const survivingTrams = [];
        for (const tram of currentTrams) {
          if ((currentTick - tram.birthTick) < LIFETIME_TICKS) {
            survivingTrams.push(tram);
          } else {
            despawnedCount++;
            totalArrivedRef.current[tram.line] = (totalArrivedRef.current[tram.line] || 0) + 1;
            changedArrived = true;
          }
        }
        currentTrams = survivingTrams;

        // 3. Neue Bahnen pro Linie spawnen
        const spawnTickCrossed = Math.floor(prevTick / spawnIntervalTicks) !== Math.floor(currentTick / spawnIntervalTicks);
        if (spawnTickCrossed) {
          for (const [lineId, lData] of Object.entries(currentLinesData)) {
            if (!lData.animationRoute || lData.animationRoute.length === 0) continue;
            const lastIdx = lData.animationRoute.length - 1;
            const oneWayTicks = lData.animationRoute.length + lData.stops.length * STOP_PAUSE_STEPS;
            const maxTrams = Math.ceil(2 * oneWayTicks / spawnIntervalTicks);
            const lineTrams = currentTrams.filter(t => t.line === lineId);

            if (lineTrams.length < maxTrams) {
              const stopZoneA = lData.stopIndices.length >= 5 ? lData.stopIndices[4] : MIN_TRAM_DISTANCE;
              const stopZoneB = lData.stopIndices.length >= 5 ? lData.stopIndices[lData.stopIndices.length - 5] : lastIdx - MIN_TRAM_DISTANCE;

              const canSpawnA = !lineTrams.some(t => t.index <= stopZoneA);
              const canSpawnB = currentSpawnMode === "both" && !lineTrams.some(t => t.index >= stopZoneB);

              if (canSpawnA) {
                currentTrams.push({
                  id: nextTramId.current++,
                  line: lineId,
                  index: 0,
                  direction: 1,
                  pause: 0,
                  pulse: 0,
                  birthTick: currentTick,
                });
                spawnedCount++;
                totalDepartedRef.current[lineId] = (totalDepartedRef.current[lineId] || 0) + 1;
                changedDeparted = true;
              }
              if (canSpawnB) {
                currentTrams.push({
                  id: nextTramId.current++,
                  line: lineId,
                  index: lastIdx,
                  direction: -1,
                  pause: 0,
                  pulse: 0,
                  birthTick: currentTick,
                });
                spawnedCount++;
                totalDepartedRef.current[lineId] = (totalDepartedRef.current[lineId] || 0) + 1;
                changedDeparted = true;
              }
            }
          }
        }
      }

      tramsRef.current = currentTrams;
      setTrams(currentTrams);

      // Sound abspielen (mit separatem Rate-Limiting für Spawn und Despawn)
      if (soundEnabledRef.current) {
        const nowMs = Date.now();
        if (spawnedCount > 0 && nowMs - lastSpawnSoundTimeRef.current >= 60) {
          lastSpawnSoundTimeRef.current = nowMs;
          playSpawnSound();
        }
        if (despawnedCount > 0 && nowMs - lastDespawnSoundTimeRef.current >= 60) {
          lastDespawnSoundTimeRef.current = nowMs;
          playDespawnSound();
        }
      }

      // Statistiken aktualisieren
      if (changedDeparted) {
        setTotalDeparted({ ...totalDepartedRef.current });
      }
      if (changedArrived) {
        setTotalArrived({ ...totalArrivedRef.current });
      }

      const newMin = Math.floor(tickCounter.current / 60);
      setSimElapsedMin(prev => (prev !== newMin ? newMin : prev));
    }, FRAME_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [linesData, playSpawnSound, playDespawnSound]);

  function getTramTrail(tram) {
    const lineInfo = linesData[tram.line];
    if (!lineInfo || !lineInfo.animationRoute || !lineInfo.animationRoute.length || tram.pause > 0) return [];
    const trailLen = Math.min(50, Math.max(15, Math.round(speedMultiplier / 15)));
    if (tram.direction === 1) {
      const start = Math.max(0, tram.index - trailLen);
      return lineInfo.animationRoute.slice(start, tram.index + 1);
    } else {
      const end = Math.min(lineInfo.animationRoute.length - 1, tram.index + trailLen);
      return lineInfo.animationRoute.slice(tram.index, end + 1);
    }
  }

  // Aktive Linien filtern
  const activeLineIds = useMemo(() => {
    return Object.keys(visibleLines).filter(id => visibleLines[id]);
  }, [visibleLines]);

  const activeTrams = useMemo(() => {
    return trams.filter(t => activeLineIds.includes(t.line));
  }, [trams, activeLineIds]);

  const statsDeparted = useMemo(() => {
    return activeLineIds.reduce((s, l) => s + (totalDeparted[l] || 0), 0);
  }, [activeLineIds, totalDeparted]);

  const statsArrived = useMemo(() => {
    return activeLineIds.reduce((s, l) => s + (totalArrived[l] || 0), 0);
  }, [activeLineIds, totalArrived]);

  return (
    <>
      {/* Simulationszeit oben mittig */}
      <div
        className="mobile-hide-statbox"
        style={{
          position: "fixed",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: "rgba(15, 15, 25, 0.75)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255, 102, 0, 0.25)",
          borderRadius: 10,
          padding: "6px 18px",
          color: "#fff",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          fontSize: 14,
          fontVariantNumeric: "tabular-nums",
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>SIM</span>
        <span style={{ fontWeight: 700 }}>
          {Math.floor(simElapsedMin / 60).toString().padStart(2, "0")}:{(simElapsedMin % 60).toString().padStart(2, "0")}
        </span>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>h:min</span>
        <button
          onClick={() => setPaused(p => !p)}
          title={paused ? "Weiter" : "Pause"}
          style={{
            marginLeft: 10,
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "1px solid rgba(255, 102, 0, 0.35)",
            background: paused ? "rgba(255, 102, 0, 0.18)" : "rgba(15, 15, 25, 0.55)",
            color: "#ff6600",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            transition: "all 0.18s",
            outline: "none",
          }}
        >
          {paused ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="4,3 13,8 4,13" fill="#ff6600" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="3" width="3" height="10" rx="1" fill="#ff6600" />
              <rect x="9" y="3" width="3" height="10" rx="1" fill="#ff6600" />
            </svg>
          )}
        </button>
      </div>

      {/* Legende & Filter oben rechts */}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {/* Linien-Filter (alphabetisch sortiert) */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#ffffff",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 14,
            maxHeight: "36vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontWeight: "bold" }}>Linien</span>
            <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
              <span
                onClick={() => {
                  const all = {};
                  LINE_CONFIGS.forEach(l => { all[l.id] = true; });
                  setVisibleLines(all);
                }}
                style={{ color: "#ff6600", cursor: "pointer", textDecoration: "underline" }}
              >
                Alle
              </span>
              <span style={{ color: "#888" }}>|</span>
              <span
                onClick={() => setVisibleLines({})}
                style={{ color: "#ff6600", cursor: "pointer", textDecoration: "underline" }}
              >
                Keine
              </span>
            </div>
          </div>
          <div style={{ overflowY: "auto", paddingRight: 4 }}>
            {LINE_CONFIGS.map(line => (
              <label key={line.id} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={!!visibleLines[line.id]}
                  onChange={e => setVisibleLines(prev => ({ ...prev, [line.id]: e.target.checked }))}
                  style={{ accentColor: line.color }}
                />
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: line.color }} />
                  {line.name}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Geschwindigkeit */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#ffffff",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>Geschwindigkeit</div>
          {[
            { label: "Echtzeit", value: 1 },
            { label: "10x", value: 10 },
            { label: "50x", value: 50 },
            { label: "100x", value: 100 },
            { label: "200x", value: 200 },
            { label: "500x", value: 500 },
            { label: "1000x", value: 1000 },
          ].map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="speed"
                checked={speedMultiplier === opt.value}
                onChange={() => setSpeedMultiplier(opt.value)}
                style={{ accentColor: "#ff6600" }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", margin: "6px 0" }} />
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={soundEnabled}
              onChange={e => setSoundEnabled(e.target.checked)}
              style={{ accentColor: "#ff6600" }}
            />
            <span>Sound</span>
          </label>
        </div>

        {/* Spawn-Modus */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#ffffff",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>Spawn-Modus</div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={spawnMode === "both"}
              onChange={() => setSpawnMode("both")}
              style={{ accentColor: "#ff6600" }}
            />
            <span>Beide Endstationen</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={spawnMode === "stoecken"}
              onChange={() => setSpawnMode("stoecken")}
              style={{ accentColor: "#ff6600" }}
            />
            <span>Eine Endstation</span>
          </label>
        </div>

        {/* Taktung */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "#ffffff",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>Taktung</div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={spawnInterval === 10}
              onChange={() => setSpawnInterval(10)}
              style={{ accentColor: "#ff6600" }}
            />
            <span>10-Minuten-Takt</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={spawnInterval === 15}
              onChange={() => setSpawnInterval(15)}
              style={{ accentColor: "#ff6600" }}
            />
            <span>15-Minuten-Takt</span>
          </label>
        </div>
      </div>

      {/* Info-Panel & Statistik unten links */}
      {(() => {
        const noLineActive = activeLineIds.length === 0;

        return (
          <div className="mobile-hide-statbox" style={{ position: "fixed", bottom: 20, left: 16, zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10 }}>
            {/* Info Button */}
            <div style={{ marginBottom: 2, position: "relative" }}>
              <button
                onClick={() => setShowInfo(prev => !prev)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid rgba(255, 102, 0, 0.35)",
                  background: showInfo ? "rgba(255, 102, 0, 0.2)" : "rgba(15, 15, 25, 0.75)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  color: "#ff6600",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  transition: "all 0.2s ease",
                  fontFamily: "Georgia, serif",
                  padding: 0,
                }}
              >
                i
              </button>

              {/* Info Popover */}
              {showInfo && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 40,
                    left: 0,
                    minWidth: 260,
                    background: "rgba(15, 15, 25, 0.88)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255, 102, 0, 0.25)",
                    borderRadius: 14,
                    padding: "16px 20px",
                    color: "#fff",
                    fontFamily: "'Segoe UI', system-ui, sans-serif",
                    fontSize: 13,
                    lineHeight: 1.6,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.55), inset 0 0 0 0.5px rgba(255,255,255,0.06)",
                    animation: "fadeIn 0.15s ease",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: "#ff6600" }}>Info</div>
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>Diese Anwendung ist ein privates, nicht-offizielles Softwareprojekt. Sie demonstriert und simuliert Fahrten der Stadtbahnlinien 1 bis 13. Der Anwender kann über die rechten Einstellungen die Simulation und Linien anpassen.</div>
                  <br />
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>Die angezeigten Fahrten sind eine Simulation und keine Echtzeit- oder Fahrplanauskunft. Nachdem eine Straßenbahn losgefahren ist, fährt sie nach 4 Stunden wieder zurück in die "Pause".</div>
                  <br />
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>Datenquellen: Haltestellenkoordinaten und aufbereitete Routendaten; für Linie 5 wurde eine Abfrage der EFA-Schnittstelle verwendet. Die übrigen Linienverläufe werden aus den hinterlegten Haltestellenkoordinaten erzeugt und interpoliert.</div>
                  <br />
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>Kartengrundlage: OpenStreetMap-Mitwirkende und CARTO. Es besteht keine offizielle Verbindung oder Unterstützung durch die ÜSTRA. ÜSTRA-Marken und Logos werden nicht verwendet.</div>
                  <br />
                  <div style={{ color: "rgba(255,255,255,0.75)" }}>Weitere Informationen: <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={{ color: "#ffb300" }}>OpenStreetMap-Lizenz</a> · <a href="https://carto.com/attributions" target="_blank" rel="noreferrer" style={{ color: "#ffb300" }}>CARTO-Attribution</a> · <a href="https://www.uestra.de" target="_blank" rel="noreferrer" style={{ color: "#ffb300" }}>ÜSTRA</a></div>
                </div>
              )}
            </div>

            <div
              style={{
                background: "rgba(15, 15, 25, 0.75)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(255, 102, 0, 0.25)",
                borderRadius: 14,
                padding: "14px 18px",
                color: "#fff",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
                fontSize: 13,
                minWidth: 250,
                boxShadow: "0 8px 32px rgba(0,0,0,0.45), inset 0 0 0 0.5px rgba(255,255,255,0.06)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: noLineActive ? "#555" : "#ff6600", boxShadow: noLineActive ? "none" : "0 0 6px #ff6600" }} />
                Live-Statistik der Simulation
              </div>

              {noLineActive ? (
                <div style={{ color: "rgba(255,255,255,0.35)", fontStyle: "italic" }}>Keine Linie aktiv</div>
              ) : (() => {
                const tramsAtStop = activeTrams.filter(t => t.pause > 0).length;
                const ratio = activeTrams.length > 0 ? (tramsAtStop / activeTrams.length * 100).toFixed(0) : 0;
                
                // Top-1-Haltestelle live (linienübergreifend aggregiert)
                let topStopName = "-", topStopCount = 0;
                const activeVisits = Object.values(stopVisitsRef.current).filter(v => activeLineIds.includes(v.lineId));
                if (activeVisits.length > 0) {
                  const aggregated = {};
                  activeVisits.forEach(v => {
                    const normName = v.normalizedName || (v.stop && v.stop.name) || "-";
                    const count = Math.floor(v.total / 2);
                    aggregated[normName] = (aggregated[normName] || 0) + count;
                  });
                  const sorted = Object.entries(aggregated).sort((a, b) => b[1] - a[1]);
                  if (sorted.length > 0 && sorted[0][1] > 0) {
                    topStopName = sorted[0][0];
                    topStopCount = sorted[0][1];
                  }
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>Losgefahren</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{statsDeparted}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>Unterwegs</span>
                      <span style={{ fontWeight: 600, color: "#ff6600", fontVariantNumeric: "tabular-nums" }}>{activeTrams.length}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>Angekommen</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{statsArrived}</span>
                    </div>

                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", margin: "4px 0" }} />

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>→ Richtung A</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{activeTrams.filter(t => t.direction === 1).length}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>← Richtung B</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{activeTrams.filter(t => t.direction === -1).length}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>An Haltestelle</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{tramsAtStop}</span>
                    </div>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", margin: "4px 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "rgba(255,255,255,0.6)" }}>Verhältnis Stop/Unterwegs</span>
                      <span style={{ fontWeight: 600, color: "#ffb300", fontVariantNumeric: "tabular-nums" }}>{ratio}%</span>
                    </div>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", margin: "4px 0 0 0" }} />
                    <div style={{ color: "rgba(255,255,255,0.6)", marginTop: 2 }}>Top-Haltestelle</div>
                    <div style={{ fontWeight: 600, color: "#ff6600", fontVariantNumeric: "tabular-nums", wordBreak: "break-word", whiteSpace: "normal", fontSize: 13, marginBottom: -2 }}>
                      {topStopName} ({topStopCount})
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Statistik-Box alle 2h */}
            {showStatsBox && (
              <div
                className="mobile-hide-statbox"
                style={{
                  background: "rgba(15, 15, 25, 0.92)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  border: "1px solid rgba(255, 102, 0, 0.25)",
                  borderRadius: 14,
                  padding: "18px 22px",
                  color: "#fff",
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                  fontSize: 13,
                  minWidth: 240,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.45), inset 0 0 0 0.5px rgba(255,255,255,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  animation: "fadeIn 0.3s",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 15, color: "#ff6600" }}>Top-5 Haltestellen (alle 2h)</div>
                {statsBoxData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {statsBoxData.map((item, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span>{i + 1}. {item.name}</span>
                        <strong style={{ color: "#ff6600" }}>{item.total}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "#aaa" }}>Keine Daten</div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <MapContainer center={[52.375, 9.75]} zoom={12} style={{ height: "100vh", width: "100%" }}>
        <FixMap />
        <TileLayer
          attribution="&copy; OpenStreetMap, &copy; CARTO"
          subdomains="abcd"
          maxZoom={20}
          url={CARTO_TILE_URL}
        />

        {/* Routen & Haltestellen pro aktiver Linie (memoized) */}
        <StaticMapLayers
          linesData={linesData}
          visibleLines={visibleLines}
          stopVisitsRef={stopVisitsRef}
          tramsRef={tramsRef}
        />

        {/* Schweif hinter den Bahnen */}
        {trams.map(tram => {
          if (!visibleLines[tram.line]) return null;
          const trail = getTramTrail(tram);
          const lineCfg = LINE_CONFIGS.find(c => c.id === tram.line);
          const color = lineCfg ? lineCfg.color : "#ff6600";
          return trail.length > 1 ? (
            <Polyline
              key={`trail-${tram.id}`}
              positions={trail}
              pathOptions={{ color: color, weight: 6, opacity: 0.7, lineCap: "round" }}
            />
          ) : null;
        })}

        {/* Bahn-Marker */}
        {trams.map(tram => {
          if (!visibleLines[tram.line]) return null;
          const lineInfo = linesData[tram.line];
          if (!lineInfo || !lineInfo.animationRoute) return null;
          const pos = lineInfo.animationRoute[tram.index];
          const lineCfg = LINE_CONFIGS.find(c => c.id === tram.line);
          const color = lineCfg ? lineCfg.color : "#ff6600";

          return pos ? (
            <CircleMarker
              key={`tram-${tram.id}`}
              center={[pos[0], pos[1]]}
              radius={tram.pulse > 0 ? 9 : 6}
              pathOptions={{ color: color, fillColor: "#ffffff", fillOpacity: 1, weight: 2 }}
            />
          ) : null;
        })}
      </MapContainer>
    </>
  );
}

export default App;